import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Modal, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FalconCardHeader from '../common/FalconCardHeader';
import AdvanceTable from '../common/advance-table/AdvanceTable';
import AdvanceTablePagination from '../common/advance-table/AdvanceTablePagination';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import { createColumnHelper } from '@tanstack/react-table';
import { toast } from 'react-toastify';
import { businessAreaService } from '../../services/api';

const BusinessAreasPage = () => {
  const [businessAreas, setBusinessAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [areaToDelete, setAreaToDelete] = useState(null);
  const [showAreaModal, setShowAreaModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    isActive: true
  });

  const columnHelper = createColumnHelper();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Área de Negocio',
        cell: ({ row }) => (
          <div>
            <h6 className="mb-0 fw-semibold">
              {row.original.name}
            </h6>
            <small className="text-muted">Código: {row.original.code}</small>
          </div>
        )
      }),
      columnHelper.accessor('description', {
        header: 'Descripción',
        cell: ({ getValue }) => (
          <span className="text-muted">{getValue() || 'Sin descripción'}</span>
        )
      }),
      columnHelper.accessor('users', {
        header: 'Usuarios',
        cell: ({ getValue }) => {
          const users = getValue() || [];
          return (
            <div className="text-center">
              <Badge bg="light" text="dark" className="fs-10">
                <FontAwesomeIcon icon="users" className="me-1" />
                {users.length}
              </Badge>
            </div>
          );
        },
        meta: { headerProps: { className: 'text-center' } }
      }),
      columnHelper.accessor('isActive', {
        header: 'Estado',
        cell: ({ getValue }) => {
          const isActive = getValue();
          return (
            <Badge bg={isActive ? 'success' : 'secondary'} className="fs-11">
              <FontAwesomeIcon 
                icon={isActive ? 'check-circle' : 'circle'} 
                className="me-1" 
              />
              {isActive ? 'Activo' : 'Inactivo'}
            </Badge>
          );
        }
      }),
      columnHelper.accessor('actions', {
        header: 'Acciones',
        cell: ({ row }) => (
          <div className="text-center">
            <Button
              variant="falcon-default"
              size="sm"
              className="me-1"
              onClick={() => handleEdit(row.original)}
              title="Editar área"
            >
              <FontAwesomeIcon icon="edit" />
            </Button>
            <Button
              variant="falcon-default"
              size="sm"
              className="text-danger"
              onClick={() => handleDelete(row.original)}
              title="Eliminar área"
            >
              <FontAwesomeIcon icon="trash" />
            </Button>
          </div>
        ),
        meta: { headerProps: { className: 'text-center' } }
      })
    ],
    []
  );

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      if (editingArea) {
        // Solo incluir isActive en edición
        const areaData = {
          name: formData.name,
          code: formData.code,
          description: formData.description,
          isActive: formData.isActive
        };
        await businessAreaService.update(editingArea.id, areaData);
        toast.success('Área de negocio actualizada exitosamente');
      } else {
        // Para nuevas áreas, EXPLÍCITAMENTE excluir isActive
        const areaData = {
          name: formData.name,
          code: formData.code,
          description: formData.description
        };
        // Verificación extra: asegurar que isActive no esté presente
        delete areaData.isActive;
        await businessAreaService.create(areaData);
        toast.success('Área de negocio creada exitosamente');
      }

      // Actualizar la lista
      setRefreshing(true);
      const areasData = await businessAreaService.getAll();
      setBusinessAreas(areasData);
      
      resetForm();
      setShowAreaModal(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar el área de negocio');
    } finally {
      setSaving(false);
      setRefreshing(false);
    }
  };

  const handleEdit = (area) => {
    setFormData({
      name: area.name,
      code: area.code,
      description: area.description || '',
      isActive: area.isActive
    });
    setEditingArea(area);
    setShowAreaModal(true);
  };

  const handleDelete = (area) => {
    if (area.users && area.users.length > 0) {
      toast.warning('No se puede eliminar un área que tiene usuarios asignados');
      return;
    }
    setAreaToDelete(area);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      
      await businessAreaService.delete(areaToDelete.id);
      toast.success('Área de negocio eliminada exitosamente');
      
      // Actualizar la lista
      setRefreshing(true);
      const areasData = await businessAreaService.getAll();
      setBusinessAreas(areasData);
      
      setShowDeleteModal(false);
      setAreaToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al eliminar el área de negocio');
    } finally {
      setDeleting(false);
      setRefreshing(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      isActive: true
    });
    setEditingArea(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowAreaModal(true);
  };

  // Configurar la tabla
  const table = useAdvanceTable({
    data: businessAreas,
    columns,
    sortable: true,
    pagination: true,
    perPage: 10
  });

  // Cargar datos iniciales - versión robusta anti-duplicación
  useEffect(() => {
    let isMounted = true;
    let hasStartedLoading = false;

    const loadData = async () => {
      // Doble verificación para evitar carreras
      if (hasStartedLoading || !isMounted) {
        return;
      }

      hasStartedLoading = true;

      try {
        if (isMounted) setLoading(true);
        
        const areasData = await businessAreaService.getAll();
        
        if (isMounted) {
          setBusinessAreas(areasData);
        }
      } catch (error) {
        if (isMounted) {
          toast.error('Error al cargar las áreas de negocio');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadData();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, []); // Sin dependencias para ejecutar solo una vez

  if (loading) {
    return (
      <Container fluid className="px-3">
        <div className="text-center py-5">
          <FontAwesomeIcon icon="spinner" spin size="2x" className="text-primary" />
          <p className="mt-3">Cargando áreas de negocio...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="px-3">
      <div className="pb-4">
        {/* Header */}
        <div className="mb-4 card card-body">
          <Row className="align-items-center">
            <Col>
              <h2 className="mb-2">
                <FontAwesomeIcon icon="folder" className="me-2 text-primary" />
                Áreas de Negocio
              </h2>
              <h5 className="text-700 fw-semi-bold">
                Gestiona las diferentes áreas de negocio de la empresa
              </h5>
            </Col>
            <Col xs="auto">
              <Button 
                variant="primary" 
                onClick={openCreateModal}
              >
                <FontAwesomeIcon icon="plus" className="me-1" />
                Nueva Área
              </Button>
            </Col>
          </Row>
        </div>

        {/* Estadísticas */}
        <Row className="g-3 mb-4">
          <Col lg={3} md={6}>
            <Card className="bg-primary text-white">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="text-white mb-0">{businessAreas.length}</h4>
                    <small>Total Áreas</small>
                  </div>
                  <FontAwesomeIcon icon="folder" size="2x" className="opacity-50" />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6}>
            <Card className="bg-success text-white">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="text-white mb-0">{businessAreas.filter(a => a.isActive).length}</h4>
                    <small>Áreas Activas</small>
                  </div>
                  <FontAwesomeIcon icon="check-circle" size="2x" className="opacity-50" />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6}>
            <Card className="bg-info text-white">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="text-white mb-0">{businessAreas.reduce((sum, a) => sum + (a.users?.length || 0), 0)}</h4>
                    <small>Usuarios Asignados</small>
                  </div>
                  <FontAwesomeIcon icon="users" size="2x" className="opacity-50" />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6}>
            <Card className="bg-warning text-white">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="text-white mb-0">{businessAreas.filter(a => !a.isActive).length}</h4>
                    <small>Áreas Inactivas</small>
                  </div>
                  <FontAwesomeIcon icon="ban" size="2x" className="opacity-50" />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabla de Áreas */}
        <Card className="shadow-sm">
          <FalconCardHeader
            title="Lista de Áreas de Negocio"
            endEl={
              <div className="d-flex gap-2">
                <Form.Control
                  type="search"
                  placeholder="Buscar áreas..."
                  className="form-control-sm"
                  style={{ width: '200px' }}
                />
                <Button variant="falcon-default" size="sm">
                  <FontAwesomeIcon icon="filter" />
                </Button>
              </div>
            }
          />
          <Card.Body className="p-0">
            <AdvanceTableProvider {...table}>
              <AdvanceTable
                headerClassName="bg-light text-900"
                bodyClassName="bg-white"
                tableProps={{
                  size: 'sm',
                  className: 'fs-10 mb-0'
                }}
              />
              <div className="px-3 pb-3">
                <AdvanceTablePagination />
              </div>
            </AdvanceTableProvider>
          </Card.Body>
        </Card>

        {/* Modal de Área */}
        <Modal show={showAreaModal} onHide={() => setShowAreaModal(false)} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={editingArea ? "edit" : "plus"} className="me-2 text-primary" />
              {editingArea ? 'Editar Área de Negocio' : 'Crear Nueva Área de Negocio'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <FontAwesomeIcon icon="folder" className="me-1 text-primary" />
                      Nombre del Área *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      required
                      placeholder="Ej: Performance, Brandformance"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <FontAwesomeIcon icon="code" className="me-1 text-primary" />
                      Código *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.code}
                      onChange={(e) => handleInputChange('code', e.target.value)}
                      required
                      placeholder="Ej: performance, brandformance"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <FontAwesomeIcon icon="file-alt" className="me-1 text-success" />
                      Descripción
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Describe el propósito y alcance de esta área de negocio..."
                    />
                  </Form.Group>
                </Col>
                {editingArea && (
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        <FontAwesomeIcon icon="cog" className="me-1 text-secondary" />
                        Estado
                      </Form.Label>
                      <Form.Select
                        value={formData.isActive}
                        onChange={(e) => handleInputChange('isActive', e.target.value === 'true')}
                      >
                        <option value={true}>Activo</option>
                        <option value={false}>Inactivo</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Las nuevas áreas se crean como activas por defecto
                      </Form.Text>
                    </Form.Group>
                  </Col>
                )}
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button 
                variant="outline-secondary" 
                onClick={() => setShowAreaModal(false)}
                disabled={saving}
              >
                <FontAwesomeIcon icon="times" className="me-1" />
                Cancelar
              </Button>
              <Button 
                type="submit" 
                variant="primary"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Guardando...</span>
                    </div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={editingArea ? "check" : "plus"} className="me-1" />
                    {editingArea ? 'Actualizar Área' : 'Crear Área'}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Modal de Confirmación de Eliminación */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon="exclamation-triangle" className="me-2 text-warning" />
              Confirmar Eliminación
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>¿Estás seguro de que deseas eliminar esta área de negocio?</p>
            {areaToDelete && (
              <div className="bg-light p-3 rounded">
                <strong>Área:</strong> {areaToDelete.name}<br/>
                <strong>Código:</strong> {areaToDelete.code}<br/>
                <strong>Descripción:</strong> {areaToDelete.description}<br/>
                <strong>Usuarios asignados:</strong> {areaToDelete.users?.length || 0}
              </div>
            )}
            <p className="text-danger mt-3 mb-0">
              <FontAwesomeIcon icon="exclamation-circle" className="me-1" />
              Esta acción no se puede deshacer.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="outline-secondary" 
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
            >
              <FontAwesomeIcon icon="times" className="me-1" />
              Cancelar
            </Button>
            <Button 
              variant="danger" 
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Eliminando...</span>
                  </div>
                  Eliminando...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon="trash" className="me-1" />
                  Eliminar Área
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Container>
  );
};

export default BusinessAreasPage; 